import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";

import { CloudProvider, InfraType, StackType } from "../../types";

import type { WizardState } from "./types";

/** Statuses that mean K8s resources have been or are being provisioned. */
export const DEPLOYED_STATUSES = new Set([
  "running",
  "stopped",
  "paused",
  "deploying",
  "provisioning",
]);

export function isDeployed(status: string): boolean {
  return DEPLOYED_STATUSES.has(status);
}

export function authHeader() {
  return {
    Authorization: `Bearer ${orgSessionManager.getToken() ?? ""}`,
  };
}

/** URL search-param keys used to persist wizard state across reloads. */
export const P = {
  STEP: "step",
  TYPE: "type",
  CLOUD: "cloud",
  INFRA: "infra",
  TARGET_DONE: "targetDone",
  TARGET_ID: "targetId",
  TEMPLATE: "template",
  NAME: "name",
  REGION: "region",
} as const;

export const STEPS = [
  "Choose type",
  "Cloud & infrastructure",
  "Choose target",
  "Choose template",
  "Review & deploy",
];

export function parseFromParams(params: URLSearchParams): {
  step: number;
  state: WizardState;
} {
  const step = Math.min(4, Math.max(0, Number(params.get(P.STEP) ?? "0")));
  return {
    step,
    state: {
      stackType: (params.get(P.TYPE) as StackType | null) ?? null,
      cloudProvider: (params.get(P.CLOUD) as CloudProvider | null) ?? null,
      infraType: (params.get(P.INFRA) as InfraType | null) ?? null,
      infraTarget: null,
      targetStepDone: params.get(P.TARGET_DONE) === "true",
      selectedTemplate: null,
      name: decodeURIComponent(params.get(P.NAME) ?? ""),
      region: decodeURIComponent(params.get(P.REGION) ?? ""),
    },
  };
}

export function serializeToParams(
  step: number,
  state: WizardState,
): Record<string, string> {
  const p: Record<string, string> = { [P.STEP]: String(step) };
  if (state.stackType) p[P.TYPE] = state.stackType;
  if (state.cloudProvider) p[P.CLOUD] = state.cloudProvider;
  if (state.infraType) p[P.INFRA] = state.infraType;
  if (state.targetStepDone) p[P.TARGET_DONE] = "true";
  if (state.infraTarget) p[P.TARGET_ID] = String(state.infraTarget.id);
  if (state.selectedTemplate) p[P.TEMPLATE] = state.selectedTemplate.slug;
  if (state.name.trim()) p[P.NAME] = encodeURIComponent(state.name);
  if (state.region) p[P.REGION] = encodeURIComponent(state.region);
  return p;
}

export function isStepValid(step: number, state: WizardState): boolean {
  if (step === 0) return state.stackType !== null;
  if (step === 1)
    return state.cloudProvider !== null && state.infraType !== null;
  if (step === 2)
    return state.infraType === "rusha_hosted" || state.targetStepDone;
  if (step === 3) return state.selectedTemplate !== null;
  if (step === 4)
    return state.name.trim().length > 0 && state.region.length > 0;
  return false;
}
